import { CreatorAction, CreatorActionType } from '@/types/types';
import { ProductRenderer } from '../ProductRenderer';
import { Loader } from './Loader';
import { getFileSrc } from '@/utils/utilities';
import { RenderUtils } from './RenderUtils';
import { MapType } from '@/api/AssetApi';
import { AssetsLib } from './AssetsLib';
import {
	MeshStandardMaterial,
	RepeatWrapping,
	SRGBColorSpace,
	LinearSRGBColorSpace,
} from 'three';

export class ActionHandler {
	private loader = Loader.getInstance();
	private assetsLib = AssetsLib.getInstance();

	constructor(private renderer: ProductRenderer) {
		this.renderer = renderer;
	}

	async dispatchAction(action: CreatorAction) {
		switch (action.type) {
			case CreatorActionType.SET_MATERIAL:
				await this.handleMaterialAction(action);
				break;
			default:
				throw new Error('UNKNOWN_ACTION_TYPE');
		}
	}

	private async handleMaterialAction(action: CreatorAction) {
		const asset = action.payload.asset;
		if (!asset || !action.payload.target) return;

		const objectTargetName = action.payload.target;

		if (!asset.maps[MapType.BASE]) {
			const material = new MeshStandardMaterial({
				color: 0x0000ff,
				...asset.options,
			});

			this.renderer.product.traverse((child) => {
				if (!RenderUtils.isMesh(child)) return;
				if (child.userData.target !== objectTargetName) return;
				child.material = material;
			});

			return;
		}

		const textures = await Promise.all(
			Object.values(MapType).map((type) => {
				if (!asset.maps[type]) return null;

				const cached = this.assetsLib.texturesCache.get(asset.maps[type].path);
				if (cached) return cached;

				return this.loader
					.loadTexture(getFileSrc(asset.maps[type].path))
					.then((texture) => {
						this.assetsLib.texturesCache.set(asset.maps[type]!.path, texture);

						return texture;
					});
			})
		);

		this.renderer.product.traverse((child) => {
			if (!RenderUtils.isMesh(child)) return;
			if (child.userData.target !== objectTargetName) return;

			const displacementScale = child.userData.displacement_scale ?? 0.0005;

			const cacheKey = `${asset.id}_${displacementScale}`;
			let material = this.assetsLib.materialCache.get(cacheKey);

			if (!material) {
				for (const texture of textures) {
					if (!texture) continue;

					texture.wrapS = RepeatWrapping;
					texture.wrapT = RepeatWrapping;
					texture.repeat.set(5, 5);
				}

				const [
					map,
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				] = textures;

				if (!map) throw new Error('MISSING_MAP');

				map.colorSpace = SRGBColorSpace;

				for (const texture of [
					roughnessMap,
					normalMap,
					displacementMap,
					metalnessMap,
					aoMap,
					lightMap,
				]) {
					if (!texture) continue;
					texture.colorSpace = LinearSRGBColorSpace;
				}

				material = new MeshStandardMaterial({
					map,
					// normalScale: new Vector2(0.025, 0.025),
					displacementMap,
					displacementScale,
					metalnessMap,
					normalMap,
					roughnessMap,
					aoMap,
					lightMap,
					...asset.options,
				});

				this.assetsLib.materialCache.set(cacheKey, material);
			}

			child.material = material;
		});
	}
}
